'use client';

import { useSearchParams } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ChipValue } from '../../../Chip/Chip';
import { useLayoutPreference } from '../../../games/FilteredGamesList/hooks/useLayoutPreference';

// Must match the `duration-300` fade transition on the games container in
// ../../../games/FilteredGamesList/styles so the layout swaps exactly when the fade-out ends.
const LAYOUT_TRANSITION_MS = 300;

type GamesListContextType = {
  gamesCounter: number;
  isGamesLoading: boolean;
  availableGameProviders: ChipValue<string>[];
  layout: 'grid' | 'list';
  isLayoutTransitioning: boolean;
  hasProvidersFiltersApplied: boolean;
  toggleLayout: () => void;
  updateGamesCounter: (count: number) => void;
  updateIsGamesLoading: (loading: boolean) => void;
  updateAvailableGameProviders: (providers: ChipValue<string>[]) => void;
};

const GamesListPageContext = createContext<GamesListContextType | null>(null);

function GamesListProvider({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const appliedFilters = Object.fromEntries(searchParams.entries());
  const [gamesCounter, setGamesCounter] = useState(0);
  const [isGamesLoading, setIsGamesLoading] = useState(false);
  const [availableGameProviders, setAvailableGameProviders] = useState<ChipValue<string>[]>([]);
  const [isLayoutTransitioning, setIsLayoutTransitioning] = useState(false);
  const { layout, updateLayout } = useLayoutPreference();
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingLayoutRef = useRef(layout);

  useEffect(() => {
    pendingLayoutRef.current = layout;
  }, [layout]);

  const clearPendingTransition = useCallback(() => {
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = undefined;
  }, []);

  useEffect(() => clearPendingTransition, [clearPendingTransition]);

  const toggleLayout = useCallback(() => {
    const nextLayout = pendingLayoutRef.current === 'grid' ? 'list' : 'grid';
    pendingLayoutRef.current = nextLayout;
    clearPendingTransition();
    setIsLayoutTransitioning(true);
    transitionTimeoutRef.current = setTimeout(() => {
      updateLayout(nextLayout);
      setIsLayoutTransitioning(false);
    }, LAYOUT_TRANSITION_MS);
  }, [updateLayout, clearPendingTransition]);

  return (
    <GamesListPageContext.Provider
      value={{
        gamesCounter,
        isGamesLoading,
        availableGameProviders,
        layout,
        isLayoutTransitioning,
        hasProvidersFiltersApplied: !!appliedFilters.gameProviders,
        toggleLayout,
        updateGamesCounter: setGamesCounter,
        updateIsGamesLoading: setIsGamesLoading,
        updateAvailableGameProviders: setAvailableGameProviders,
      }}
    >
      {children}
    </GamesListPageContext.Provider>
  );
}

function useGamesList() {
  const context = useContext(GamesListPageContext);
  if (!context) {
    throw new Error('useGamesList must be used within GamesListProvider');
  }
  return context;
}

export { GamesListPageContext, GamesListProvider, useGamesList };
